import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Bike, 
  Wrench, 
  Euro, 
  TrendingUp,
  Clock,
  ArrowRight,
  Layers,
  Calendar,
  User,
  Users,
  Package,
  FileSpreadsheet,
  FileText,
  Filter,
  RefreshCw,
  Search,
  X,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import FleetHistory from '../components/FleetHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Date filter state
  const [dateFilters, setDateFilters] = useState({
    startDate: '',
    endDate: ''
  });
  const [isFiltered, setIsFiltered] = useState(false);
  
  // Material consumption state
  const [materialConsumption, setMaterialConsumption] = useState(null);
  const [materialLoading, setMaterialLoading] = useState(false);
  const [materialDateFilters, setMaterialDateFilters] = useState({
    startDate: '',
    endDate: ''
  });

  // Fleet filter state
  const [fleetModelFilter, setFleetModelFilter] = useState('all');
  const [fleetSizeFilter, setFleetSizeFilter] = useState('all');
  const [fleetSearchQuery, setFleetSearchQuery] = useState('');
  
  // Fleet status filter state (new feature)
  const [fleetStatusFilters, setFleetStatusFilters] = useState({
    activa: true,
    en_taller: true,
    en_revision: true,
    rota: true,
    prestada: true
  });
  const [fleetViewData, setFleetViewData] = useState(null);
  const [fleetViewLoading, setFleetViewLoading] = useState(false);
  const [loanAlerts, setLoanAlerts] = useState([]);

  // User revisions modal state
  const [userRevisionsModal, setUserRevisionsModal] = useState({
    isOpen: false,
    loading: false,
    mechanicId: null,
    mechanicName: '',
    revisions: [],
    summary: null
  });

  const fetchUserRevisions = async (mechanicId, mechanicName) => {
    setUserRevisionsModal({
      isOpen: true,
      loading: true,
      mechanicId,
      mechanicName,
      revisions: [],
      summary: null
    });

    try {
      const token = localStorage.getItem('token');
      let url = `${API}/stats/user-revisions/${mechanicId}`;
      const params = new URLSearchParams();
      
      // Apply same date filters as dashboard
      if (dateFilters.startDate) params.append('start_date', dateFilters.startDate);
      if (dateFilters.endDate) params.append('end_date', dateFilters.endDate);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUserRevisionsModal(prev => ({
        ...prev,
        loading: false,
        revisions: response.data.revisions,
        summary: response.data.summary
      }));
    } catch (error) {
      console.error('Error fetching user revisions:', error);
      setUserRevisionsModal(prev => ({
        ...prev,
        loading: false,
        revisions: [],
        summary: null
      }));
    }
  };

  const closeUserRevisionsModal = () => {
    setUserRevisionsModal({
      isOpen: false,
      loading: false,
      mechanicId: null,
      mechanicName: '',
      revisions: [],
      summary: null
    });
  };

  useEffect(() => {
    fetchStats();
    fetchMaterialConsumption();
    fetchFleetView();
    fetchLoanAlerts();
  }, []);

  // Fetch fleet view data when status filters change
  useEffect(() => {
    fetchFleetView();
  }, [fleetStatusFilters]);

  const fetchFleetView = async () => {
    setFleetViewLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Build status query parameter
      const activeStatuses = Object.entries(fleetStatusFilters)
        .filter(([_, isActive]) => isActive)
        .map(([status, _]) => status);
      
      const statusParam = activeStatuses.length > 0 
        ? activeStatuses.join(',') 
        : 'activa,en_taller';
      
      const response = await axios.get(
        `${API}/stats/fleet-by-model?statuses=${encodeURIComponent(statusParam)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFleetViewData(response.data);
    } catch (error) {
      console.error('Error fetching fleet view:', error);
    } finally {
      setFleetViewLoading(false);
    }
  };

  const toggleFleetStatusFilter = (status) => {
    setFleetStatusFilters(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  const fetchStats = async (startDate = '', endDate = '') => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await axios.get(`${API}/stats/dashboard?${params.toString()}`);
      setStats(response.data);
      setIsFiltered(!!(startDate || endDate));
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLoanAlerts = async () => {
    try {
      const res = await axios.get(`${API}/bicycles/loan-alerts`);
      setLoanAlerts(res.data);
    } catch {}
  };


  const fetchMaterialConsumption = async (startDate = '', endDate = '') => {
    setMaterialLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await axios.get(`${API}/stats/material-consumption?${params.toString()}`);
      setMaterialConsumption(response.data);
    } catch (error) {
      console.error('Error fetching material consumption:', error);
    } finally {
      setMaterialLoading(false);
    }
  };

  const handleFilterStats = () => {
    fetchStats(dateFilters.startDate, dateFilters.endDate);
  };

  const clearStatsFilters = () => {
    setDateFilters({ startDate: '', endDate: '' });
    fetchStats();
  };

  const handleFilterMaterials = () => {
    fetchMaterialConsumption(materialDateFilters.startDate, materialDateFilters.endDate);
  };

  const clearMaterialFilters = () => {
    setMaterialDateFilters({ startDate: '', endDate: '' });
    fetchMaterialConsumption();
  };

  const exportToCSV = () => {
    if (!materialConsumption?.materials?.length) {
      toast.error('No hay datos para exportar');
      return;
    }

    const headers = ['Material', 'Categoría', 'Cantidad', 'Nº Usos', 'Precio Unit.', 'Total Gastado'];
    const rows = materialConsumption.materials.map(m => [
      m.name,
      m.category,
      m.quantity,
      m.usage_count,
      m.current_price.toFixed(2),
      m.total_spent.toFixed(2)
    ]);
    
    rows.push([]);
    rows.push(['RESUMEN']);
    rows.push(['Total Revisiones', materialConsumption.summary.total_revisions]);
    rows.push(['Total Materiales', materialConsumption.summary.total_materials_cost.toFixed(2) + ' €']);
    rows.push(['Período', `${materialDateFilters.startDate || 'Inicio'} - ${materialDateFilters.endDate || 'Hoy'}`]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `consumo_materiales_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Archivo CSV descargado');
  };

  const exportToPDF = () => {
    if (!materialConsumption?.materials?.length) {
      toast.error('No hay datos para exportar');
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Informe de Consumo de Materiales</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1e293b; border-bottom: 2px solid #f97316; padding-bottom: 10px; }
          .summary { background: #f8fafc; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
          .summary-item { text-align: center; }
          .summary-value { font-size: 24px; font-weight: bold; color: #f97316; }
          .summary-label { color: #64748b; font-size: 12px; text-transform: uppercase; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #f1f5f9; text-align: left; padding: 12px; font-size: 12px; text-transform: uppercase; color: #475569; }
          td { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; }
          tr:nth-child(even) { background: #f8fafc; }
          .text-right { text-align: right; }
          .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 12px; }
          @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <h1>VeloFlux - Informe de Consumo de Materiales</h1>
        <p style="color: #64748b;">Período: ${materialDateFilters.startDate || 'Desde el inicio'} - ${materialDateFilters.endDate || 'Hasta hoy'}</p>
        
        <div class="summary">
          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-value">${materialConsumption.summary.total_revisions}</div>
              <div class="summary-label">Revisiones</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${materialConsumption.summary.unique_materials}</div>
              <div class="summary-label">Materiales Diferentes</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${formatCurrency(materialConsumption.summary.total_materials_cost)}</div>
              <div class="summary-label">Total Gastado</div>
            </div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Material</th>
              <th>Categoría</th>
              <th class="text-right">Cantidad</th>
              <th class="text-right">Nº Usos</th>
              <th class="text-right">Precio Unit.</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${materialConsumption.materials.map(m => `
              <tr>
                <td><strong>${m.name}</strong></td>
                <td>${m.category}</td>
                <td class="text-right">${m.quantity}</td>
                <td class="text-right">${m.usage_count}</td>
                <td class="text-right">${formatCurrency(m.current_price)}</td>
                <td class="text-right"><strong>${formatCurrency(m.total_spent)}</strong></td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          Generado el ${new Date().toLocaleString('es-ES')} | VeloFlux Fleet Manager
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
    toast.success('Generando PDF para imprimir');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard-page">
      {/* Header with date filter */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold font-['Barlow_Condensed'] tracking-tight text-slate-900">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Vista general de tu flota de bicicletas
            {isFiltered ? (
              <Badge variant="outline" className="ml-2 bg-orange-50 text-orange-700 border-orange-200">
                Filtrado
              </Badge>
            ) : (
              <span className="text-orange-500 ml-1">· Año {stats?.filter_year}</span>
            )}
          </p>
        </div>
        
        {/* Date Filter */}
        <Card className="border-slate-200">
          <CardContent className="p-3">
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Desde</Label>
                <Input
                  type="date"
                  value={dateFilters.startDate}
                  onChange={(e) => setDateFilters({ ...dateFilters, startDate: e.target.value })}
                  className="h-8 w-36"
                  data-testid="dashboard-filter-start"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Hasta</Label>
                <Input
                  type="date"
                  value={dateFilters.endDate}
                  onChange={(e) => setDateFilters({ ...dateFilters, endDate: e.target.value })}
                  className="h-8 w-36"
                  data-testid="dashboard-filter-end"
                />
              </div>
              <Button
                size="sm"
                onClick={handleFilterStats}
                className="bg-orange-500 hover:bg-orange-600 h-8"
                data-testid="dashboard-filter-btn"
              >
                <Filter size={14} className="mr-1" /> Filtrar
              </Button>
              {isFiltered && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearStatsFilters}
                  className="h-8"
                >
                  <RefreshCw size={14} className="mr-1" /> Año actual
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats cards - 4 cards */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isAdmin ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6`}>
        {/* Flota Activa */}
        <Card className="card-hover border-slate-200" data-testid="stat-active-fleet">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Flota Activa
                </p>
                <p className="text-3xl font-bold text-blue-600 mt-2 font-['Barlow_Condensed']">
                  {stats?.total_active_fleet || 0}
                </p>
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-600">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                    <span className="font-medium">{stats?.normal_active_count || 0}</span>
                    <span className="text-slate-400">normales</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span>
                    <span className="font-medium">{stats?.electric_active_count || 0}</span>
                    <span className="text-slate-400">eléctricas</span>
                  </span>
                </div>
                {stats?.bicycles_loaned > 0 && (
                  <p className="text-[11px] text-purple-600 mt-1" data-testid="loaned-count">
                    Incluye {stats.bicycles_loaned} prestada{stats.bicycles_loaned === 1 ? '' : 's'}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Bike className="text-blue-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Revisiones del período */}
        <Card className="card-hover border-slate-200" data-testid="stat-revisions-year">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Revisiones {isFiltered ? 'Período' : stats?.filter_year}
                </p>
                <p className="text-3xl font-bold text-slate-900 mt-2 font-['Barlow_Condensed']">
                  {stats?.revisions_year || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Total histórico: {stats?.revisions_all || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="text-slate-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Coste del período */}
        <Card className="card-hover border-slate-200" data-testid="stat-cost-year">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Coste {isFiltered ? 'Período' : stats?.filter_year}
                </p>
                <p className="text-3xl font-bold text-slate-900 mt-2 font-['Barlow_Condensed']">
                  {formatCurrency(stats?.cost_year || 0)}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Total histórico: {formatCurrency(stats?.cost_all || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Euro className="text-green-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Valor Flota - Admin only */}
        {isAdmin && (
          <Card className="card-hover border-slate-200 bg-gradient-to-br from-blue-50 to-slate-50" data-testid="stat-fleet-value">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                    Valor Flota Activa
                  </p>
                  <p className="text-3xl font-bold text-blue-700 mt-2 font-['Barlow_Condensed']">
                    {formatCurrency(stats?.total_fleet_value || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-200 rounded-xl flex items-center justify-center">
                  <Layers className="text-blue-700" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Histórico de flota */}
      <FleetHistory isAdmin={isAdmin} />

      {/* Monthly costs chart - Full width */}
      <Card className="border-slate-200" data-testid="monthly-costs-chart">
        <CardHeader>
          <CardTitle className="text-xl font-['Barlow_Condensed']">Costes Mensuales (Últimos 6 meses)</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.monthly_costs?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.monthly_costs}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickFormatter={(value) => {
                    const [year, month] = value.split('-');
                    return new Date(year, month - 1).toLocaleDateString('es-ES', { month: 'short' });
                  }}
                />
                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), 'Coste']}
                  labelFormatter={(label) => {
                    const [year, month] = label.split('-');
                    return new Date(year, month - 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
                  }}
                />
                <Bar dataKey="total" fill="#F97316" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-slate-400">
              Sin datos de costes todavía
            </div>
          )}
        </CardContent>
      </Card>

      {/* Today's Revisions */}
      <Card className="border-slate-200 bg-gradient-to-r from-blue-50 to-cyan-50" data-testid="today-revisions">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl font-['Barlow_Condensed'] flex items-center gap-2">
            <Calendar size={20} className="text-blue-600" />
            Revisiones de Hoy
            <Badge className="ml-2 bg-blue-600 text-white">
              {stats?.today_revisions_count || 0}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.today_revisions?.length > 0 ? (
            <div className="space-y-3">
              {stats.today_revisions.map((revision, index) => (
                <div 
                  key={index} 
                  onClick={() => navigate(`/bicycles/${revision.bicycle_id}`)}
                  className="flex items-start gap-3 p-4 bg-white rounded-lg border border-blue-100 shadow-sm cursor-pointer hover:bg-blue-50 hover:border-blue-300 hover:shadow-md transition-all duration-200 group"
                  data-testid={`today-revision-${index}`}
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                    <Bike size={18} className="text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 group-hover:text-blue-700 transition-colors">
                      {revision.description}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        <User size={12} />
                        {revision.mechanic_name}
                      </span>
                      <span className="text-xs text-slate-500 font-medium">
                        Bici #{revision.bicycle_number || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {formatCurrency(revision.total_cost)}
                    </Badge>
                    <ArrowRight size={16} className="text-slate-300 group-hover:text-blue-500 transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Calendar size={32} className="mx-auto mb-2 opacity-50" />
              <p>No hay revisiones hoy</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loan Alerts */}
      {loanAlerts.length > 0 && (
        <Card className="border-red-200 bg-gradient-to-r from-red-50 to-amber-50" data-testid="loan-alerts">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-['Barlow_Condensed'] flex items-center gap-2">
              <Clock size={20} className="text-red-600" />
              Alertas de Préstamos
              <Badge className="ml-2 bg-red-600 text-white">
                {loanAlerts.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {loanAlerts.map((alert) => (
                <div
                  key={alert.id}
                  onClick={() => navigate(`/bicycles/${alert.id}`)}
                  className={`flex items-start gap-3 p-4 bg-white rounded-lg border shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 group ${
                    alert.alert_type === 'overdue' ? 'border-red-300' : 'border-amber-300'
                  }`}
                  data-testid={`loan-alert-${alert.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    alert.alert_type === 'overdue' ? 'bg-red-100' : 'bg-amber-100'
                  }`}>
                    <Bike size={18} className={alert.alert_type === 'overdue' ? 'text-red-600' : 'text-amber-600'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">
                      Bici #{alert.number || alert.codigo_interno} — {alert.model || 'Sin modelo'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      <User size={12} className="inline mr-1" />
                      Prestada a: {alert.loan_person_name}
                    </p>
                  </div>
                  <Badge className={
                    alert.alert_type === 'overdue' ? 'bg-red-600 text-white' :
                    alert.alert_type === 'due_today' ? 'bg-amber-600 text-white' :
                    'bg-amber-100 text-amber-800 border border-amber-300'
                  }>
                    {alert.alert_type === 'overdue' ? 'VENCIDO' :
                     alert.alert_type === 'due_today' ? 'HOY' : 'MAÑANA'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revisions by User */}
      <Card className="border-slate-200 bg-gradient-to-r from-purple-50 to-indigo-50" data-testid="revisions-by-user">
        <CardHeader>
          <CardTitle className="text-xl font-['Barlow_Condensed'] flex items-center gap-2">
            <Users size={20} className="text-purple-600" />
            Revisiones por Usuario
            <span className="text-sm font-normal text-slate-400 ml-2">
              ({isFiltered ? 'Período filtrado' : stats?.filter_year})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.revisions_by_user?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {stats.revisions_by_user.map((user, index) => (
                <div 
                  key={user.mechanic_id || index}
                  onClick={() => fetchUserRevisions(user.mechanic_id, user.mechanic_name)}
                  className="bg-white rounded-lg p-4 border border-purple-100 shadow-sm hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer group"
                  data-testid={`user-revision-card-${index}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-purple-200 transition-colors">
                      <User size={18} className="text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate group-hover:text-purple-700 transition-colors">{user.mechanic_name}</p>
                      <p className="text-xs text-slate-400">Click para ver detalle</p>
                    </div>
                    <ChevronRight size={18} className="text-slate-300 group-hover:text-purple-500 transition-colors" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-purple-50 rounded-lg p-2 text-center">
                      <p className="text-2xl font-bold text-purple-700 font-['Barlow_Condensed']">
                        {user.count}
                      </p>
                      <p className="text-xs text-purple-600 uppercase font-semibold">
                        {user.count === 1 ? 'Revisión' : 'Revisiones'}
                      </p>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-indigo-700 font-['Barlow_Condensed']">
                        {formatCurrency(user.total_cost)}
                      </p>
                      <p className="text-xs text-indigo-600 uppercase font-semibold">Coste Total</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Users size={32} className="mx-auto mb-2 opacity-50" />
              <p>No hay revisiones en este período</p>
            </div>
          )}
          
          {/* Summary */}
          {stats?.revisions_by_user?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-purple-100 flex flex-wrap justify-between items-center gap-4 text-sm">
              <span className="text-slate-600">
                <strong>{stats.revisions_by_user.length}</strong> usuarios han realizado revisiones
              </span>
              <span className="text-slate-600">
                Total: <strong>{stats.revisions_by_user.reduce((sum, u) => sum + u.count, 0)}</strong> revisiones · <strong>{formatCurrency(stats.revisions_by_user.reduce((sum, u) => sum + u.total_cost, 0))}</strong>
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top materials */}
        <Card className="border-slate-200" data-testid="top-materials">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-['Barlow_Condensed']">
              Materiales Más Usados
              <span className="text-sm font-normal text-slate-400 ml-2">
                ({isFiltered ? 'Período filtrado' : stats?.filter_year})
              </span>
            </CardTitle>
            <Link 
              to="/materials" 
              className="text-sm text-orange-500 hover:text-orange-600 flex items-center gap-1"
            >
              Ver todos <ArrowRight size={14} />
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.top_materials?.length > 0 ? (
              <div className="space-y-3">
                {stats.top_materials.slice(0, 5).map((material, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-xs font-semibold">
                        {index + 1}
                      </span>
                      <span className="font-medium text-slate-700">{material.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-900">
                        {formatCurrency(material.spent)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {material.quantity} unidades
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">Sin materiales registrados en este período</p>
            )}
          </CardContent>
        </Card>

        {/* Recent revisions */}
        <Card className="border-slate-200" data-testid="recent-revisions">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-['Barlow_Condensed']">Revisiones Recientes</CardTitle>
            <Link 
              to="/bicycles" 
              className="text-sm text-orange-500 hover:text-orange-600 flex items-center gap-1"
            >
              Ver bicis <ArrowRight size={14} />
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recent_revisions?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_revisions.map((revision, index) => (
                  <div 
                    key={index} 
                    onClick={() => navigate(`/bicycles/${revision.bicycle_id}`)}
                    className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-orange-50 hover:shadow-sm transition-all duration-200 group"
                    data-testid={`recent-revision-${index}`}
                  >
                    <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                      <Bike size={16} className="text-slate-500 group-hover:text-orange-600 transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate group-hover:text-orange-700 transition-colors">
                        {revision.description}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Por {revision.mechanic_name} · {formatDate(revision.date)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                        {formatCurrency(revision.total_cost)}
                      </Badge>
                      <ArrowRight size={14} className="text-slate-300 group-hover:text-orange-500 transition-colors" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">Sin revisiones todavía</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fleet by Model - Admin only */}
      {isAdmin && (
        <Card className="border-slate-200" data-testid="fleet-by-model">
          <CardHeader>
            <div className="flex flex-col gap-4">
              <CardTitle className="text-xl font-['Barlow_Condensed'] flex items-center gap-2">
                <Layers size={20} className="text-blue-600" />
                Flota por Modelo y Talla
              </CardTitle>
              
              {/* Status Filters */}
              <div className="flex flex-wrap gap-2 pb-3 border-b">
                <span className="text-xs text-slate-500 font-semibold mr-2 self-center">Filtrar por estado:</span>
                {[
                  { key: 'activa', label: 'Activas', color: 'bg-green-100 text-green-700 border-green-300' },
                  { key: 'en_taller', label: 'En Taller', color: 'bg-blue-100 text-blue-700 border-blue-300' },
                  { key: 'en_revision', label: 'En Revisión', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
                  { key: 'rota', label: 'Rotas', color: 'bg-red-100 text-red-700 border-red-300' },
                  { key: 'prestada', label: 'Prestadas', color: 'bg-purple-100 text-purple-700 border-purple-300' }
                ].map(({ key, label, color }) => (
                  <button
                    key={key}
                    onClick={() => toggleFleetStatusFilter(key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      fleetStatusFilters[key]
                        ? color
                        : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100'
                    }`}
                    data-testid={`fleet-status-filter-${key.replace(' ', '-')}`}
                  >
                    {label}
                    {fleetViewData?.status_counts?.[key] !== undefined && (
                      <span className="ml-1.5 opacity-75">({fleetViewData.status_counts[key] || 0})</span>
                    )}
                  </button>
                ))}
              </div>
              
              {/* Fleet Search and Filters */}
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px] max-w-[300px]">
                  <Label className="text-xs text-slate-500 mb-1 block">Buscar</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <Input
                      placeholder="Buscar modelo..."
                      value={fleetSearchQuery}
                      onChange={(e) => setFleetSearchQuery(e.target.value)}
                      className="pl-9 h-9"
                      data-testid="fleet-search-input"
                    />
                    {fleetSearchQuery && (
                      <button
                        onClick={() => setFleetSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="w-[180px]">
                  <Label className="text-xs text-slate-500 mb-1 block">Modelo</Label>
                  <Select value={fleetModelFilter} onValueChange={setFleetModelFilter}>
                    <SelectTrigger className="h-9" data-testid="fleet-model-filter">
                      <SelectValue placeholder="Todos los modelos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los modelos</SelectItem>
                      {(fleetViewData?.unique_models || stats?.unique_models || []).map(model => (
                        <SelectItem key={model} value={model}>{model}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[120px]">
                  <Label className="text-xs text-slate-500 mb-1 block">Talla</Label>
                  <Select value={fleetSizeFilter} onValueChange={setFleetSizeFilter}>
                    <SelectTrigger className="h-9" data-testid="fleet-size-filter">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {(fleetViewData?.unique_sizes || stats?.unique_sizes || []).map(size => (
                        <SelectItem key={size} value={size}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(fleetModelFilter !== 'all' || fleetSizeFilter !== 'all' || fleetSearchQuery) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFleetModelFilter('all');
                      setFleetSizeFilter('all');
                      setFleetSearchQuery('');
                    }}
                    className="h-9"
                  >
                    <RefreshCw size={14} className="mr-1" /> Limpiar
                  </Button>
                )}
              </div>
              
              {/* Quick Stats by Size */}
              {fleetViewData?.fleet_by_model_size?.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <span className="text-xs text-slate-500 font-semibold mr-2 self-center">Por talla:</span>
                  {(() => {
                    // Calculate size totals from filtered data
                    const sizeTotals = {};
                    fleetViewData.fleet_by_model_size.forEach(item => {
                      if (item.size) {
                        sizeTotals[item.size] = (sizeTotals[item.size] || 0) + item.count;
                      }
                    });
                    return Object.entries(sizeTotals)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([size, count]) => (
                        <Badge 
                          key={size} 
                          variant="outline" 
                          className={`cursor-pointer transition-colors ${
                            fleetSizeFilter === size 
                              ? 'bg-blue-100 text-blue-700 border-blue-300' 
                              : 'bg-slate-50 hover:bg-slate-100'
                          }`}
                          onClick={() => setFleetSizeFilter(fleetSizeFilter === size ? 'all' : size)}
                        >
                          {size}: <strong className="ml-1">{count}</strong>
                        </Badge>
                      ));
                  })()}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {fleetViewLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner w-6 h-6" />
              </div>
            ) : (() => {
              // Use fleet view data
              let fleetData = fleetViewData?.fleet_by_model_size || [];
              
              // Apply local filters
              if (fleetSearchQuery) {
                const query = fleetSearchQuery.toLowerCase();
                fleetData = fleetData.filter(item => 
                  item.model?.toLowerCase().includes(query)
                );
              }
              
              if (fleetModelFilter !== 'all') {
                fleetData = fleetData.filter(item => item.model === fleetModelFilter);
              }
              
              if (fleetSizeFilter !== 'all') {
                fleetData = fleetData.filter(item => item.size === fleetSizeFilter);
              }
              
              const totalFiltered = fleetData.reduce((sum, item) => sum + item.count, 0);
              
              // Group by model for display
              const groupedByModel = {};
              fleetData.forEach(item => {
                if (!groupedByModel[item.model]) {
                  groupedByModel[item.model] = {
                    model: item.model,
                    total: 0,
                    sizes: [],
                    avg_purchase_price: fleetViewData?.model_avg_prices?.[item.model] || 0
                  };
                }
                groupedByModel[item.model].total += item.count;
                groupedByModel[item.model].sizes.push({
                  size: item.size,
                  count: item.count,
                  avg_purchase_price: item.avg_purchase_price
                });
              });
              
              // Sort sizes within each model
              Object.values(groupedByModel).forEach(group => {
                group.sizes.sort((a, b) => {
                  const aNum = parseInt(a.size) || 0;
                  const bNum = parseInt(b.size) || 0;
                  if (aNum !== bNum) return aNum - bNum;
                  return a.size.localeCompare(b.size);
                });
              });
              
              const modelGroups = Object.values(groupedByModel).sort((a, b) => b.total - a.total);
              
              return (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs font-bold uppercase text-slate-500">Modelo</TableHead>
                          <TableHead className="text-xs font-bold uppercase text-slate-500 text-center">Total</TableHead>
                          <TableHead className="text-xs font-bold uppercase text-slate-500 text-right">Precio Medio</TableHead>
                          <TableHead className="text-xs font-bold uppercase text-slate-500">Desglose por Talla</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {modelGroups.length > 0 ? (
                          modelGroups.map((group, index) => (
                            <TableRow key={index} className="table-row-hover align-top">
                              <TableCell className="font-medium text-slate-900 py-4">
                                {group.model}
                              </TableCell>
                              <TableCell className="text-center py-4">
                                <Badge className="bg-blue-600 text-white text-base px-3 py-1">
                                  {group.total}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right py-4">
                                {group.avg_purchase_price > 0 ? (
                                  <span className="font-semibold text-green-600">
                                    {formatCurrency(group.avg_purchase_price)}
                                  </span>
                                ) : (
                                  <span className="text-slate-400 text-sm">Sin datos</span>
                                )}
                              </TableCell>
                              <TableCell className="py-4">
                                <div className="flex flex-wrap gap-2">
                                  {group.sizes.map((sizeItem, sIdx) => (
                                    <div 
                                      key={sIdx} 
                                      className="inline-flex items-center gap-1 bg-slate-100 rounded-lg px-3 py-1.5 text-sm"
                                    >
                                      <span className="text-slate-600">Talla</span>
                                      <span className="font-semibold text-slate-800">{sizeItem.size}</span>
                                      <ChevronRight size={14} className="text-slate-400" />
                                      <span className="font-bold text-blue-600">{sizeItem.count}</span>
                                    </div>
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                              No se encontraron bicicletas con los filtros seleccionados
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="p-4 border-t bg-slate-50">
                    <div className="flex flex-wrap justify-between items-center gap-4 text-sm">
                      <span className="text-slate-600">
                        {(fleetModelFilter !== 'all' || fleetSizeFilter !== 'all' || fleetSearchQuery) ? (
                          <>Mostrando: <strong>{modelGroups.length}</strong> modelos (<strong>{totalFiltered}</strong> bicis)</>
                        ) : (
                          <>Total: <strong>{modelGroups.length}</strong> modelos diferentes</>
                        )}
                      </span>
                      <span className="text-slate-600">
                        Total filtrado: <strong>{fleetViewData?.total_count || 0}</strong> bicis
                      </span>
                    </div>
                  </div>
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Material Consumption Report - Admin only */}
      {isAdmin && (
        <Card className="border-slate-200" data-testid="material-consumption">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-xl font-['Barlow_Condensed'] flex items-center gap-2">
                <Package size={20} className="text-green-600" />
                Consumo de Materiales
              </CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportToCSV}
                  disabled={!materialConsumption?.materials?.length}
                  className="gap-2"
                  data-testid="export-csv-btn"
                >
                  <FileSpreadsheet size={16} />
                  Excel/CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportToPDF}
                  disabled={!materialConsumption?.materials?.length}
                  className="gap-2"
                  data-testid="export-pdf-btn"
                >
                  <FileText size={16} />
                  PDF
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Date Filters */}
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1">
                      <Calendar size={12} />
                      Fecha Inicio
                    </Label>
                    <Input
                      type="date"
                      value={materialDateFilters.startDate}
                      onChange={(e) => setMaterialDateFilters({ ...materialDateFilters, startDate: e.target.value })}
                      data-testid="material-filter-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1">
                      <Calendar size={12} />
                      Fecha Fin
                    </Label>
                    <Input
                      type="date"
                      value={materialDateFilters.endDate}
                      onChange={(e) => setMaterialDateFilters({ ...materialDateFilters, endDate: e.target.value })}
                      data-testid="material-filter-end-date"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleFilterMaterials}
                    className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
                    data-testid="material-apply-filter-btn"
                  >
                    <Filter size={16} />
                    Filtrar
                  </Button>
                  {(materialDateFilters.startDate || materialDateFilters.endDate) && (
                    <Button
                      variant="outline"
                      onClick={clearMaterialFilters}
                      data-testid="material-clear-filter-btn"
                    >
                      Limpiar
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            {materialConsumption && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700 font-['Barlow_Condensed']">
                    {materialConsumption.summary.total_revisions}
                  </p>
                  <p className="text-xs text-blue-600 uppercase font-semibold">Revisiones</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700 font-['Barlow_Condensed']">
                    {materialConsumption.summary.unique_materials}
                  </p>
                  <p className="text-xs text-green-600 uppercase font-semibold">Materiales</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-orange-700 font-['Barlow_Condensed']">
                    {formatCurrency(materialConsumption.summary.total_materials_cost)}
                  </p>
                  <p className="text-xs text-orange-600 uppercase font-semibold">Total Gastado</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-700 font-['Barlow_Condensed']">
                    {formatCurrency(materialConsumption.summary.total_cost)}
                  </p>
                  <p className="text-xs text-purple-600 uppercase font-semibold">Coste Total Rev.</p>
                </div>
              </div>
            )}

            {/* Materials Table */}
            {materialLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner w-6 h-6" />
              </div>
            ) : materialConsumption?.materials?.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-bold uppercase text-slate-500">Material</TableHead>
                      <TableHead className="text-xs font-bold uppercase text-slate-500">Categoría</TableHead>
                      <TableHead className="text-xs font-bold uppercase text-slate-500 text-center">Cantidad</TableHead>
                      <TableHead className="text-xs font-bold uppercase text-slate-500 text-center">Nº Usos</TableHead>
                      <TableHead className="text-xs font-bold uppercase text-slate-500 text-right">Precio Unit.</TableHead>
                      <TableHead className="text-xs font-bold uppercase text-slate-500 text-right">Total Gastado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materialConsumption.materials.map((material, index) => (
                      <TableRow key={index} className="table-row-hover">
                        <TableCell className="font-medium text-slate-900">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs font-semibold">
                              {index + 1}
                            </span>
                            {material.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-slate-50">
                            {material.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center font-semibold text-slate-700">
                          {material.quantity}
                        </TableCell>
                        <TableCell className="text-center text-slate-600">
                          {material.usage_count}
                        </TableCell>
                        <TableCell className="text-right text-slate-600">
                          {formatCurrency(material.current_price)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-semibold text-orange-600">
                            {formatCurrency(material.total_spent)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Package size={48} className="mx-auto mb-4 opacity-50" />
                <p>No hay consumo de materiales en el período seleccionado</p>
                <p className="text-sm mt-1">Selecciona otras fechas o añade revisiones</p>
              </div>
            )}

            {/* Period Info */}
            {materialConsumption && (
              <div className="mt-4 pt-4 border-t text-sm text-slate-500 flex justify-between items-center">
                <span>
                  Período: {materialDateFilters.startDate || 'Desde el inicio'} → {materialDateFilters.endDate || 'Hasta hoy'}
                </span>
                <span>
                  {materialConsumption.materials.length} materiales diferentes
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* User Revisions Modal */}
      <Dialog open={userRevisionsModal.isOpen} onOpenChange={(open) => !open && closeUserRevisionsModal()}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="text-2xl font-['Barlow_Condensed'] flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <User size={24} className="text-purple-600" />
              </div>
              <div>
                <span className="text-purple-700">Revisiones de {userRevisionsModal.mechanicName}</span>
                {userRevisionsModal.summary && (
                  <div className="text-sm font-normal text-slate-500 mt-1">
                    {userRevisionsModal.summary.total_revisions} revisiones · {formatCurrency(userRevisionsModal.summary.total_cost)} total
                  </div>
                )}
              </div>
            </DialogTitle>
            {isFiltered && (
              <div className="text-sm text-slate-500 mt-2 flex items-center gap-2">
                <Calendar size={14} />
                <span>
                  Período: {dateFilters.startDate || 'Inicio'} → {dateFilters.endDate || 'Hoy'}
                </span>
              </div>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {userRevisionsModal.loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="animate-spin text-purple-500" size={32} />
              </div>
            ) : userRevisionsModal.revisions.length > 0 ? (
              <div className="space-y-3">
                {userRevisionsModal.revisions.map((revision, index) => (
                  <div 
                    key={revision.id || index}
                    className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:border-purple-200 transition-colors"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      {/* Left side: date and bike info */}
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                          <Calendar size={14} />
                          <span>{new Date(revision.date).toLocaleDateString('es-ES', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric' 
                          })}</span>
                        </div>
                        <div 
                          className="font-semibold text-slate-800 hover:text-purple-700 cursor-pointer flex items-center gap-2"
                          onClick={() => {
                            closeUserRevisionsModal();
                            navigate(`/bicycles/${revision.bicycle_id}`);
                          }}
                        >
                          <Bike size={16} className="text-purple-500" />
                          <span>{revision.bicycle_number}</span>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-600 font-normal">{revision.bicycle_model}</span>
                          <Badge variant="outline" className="ml-1 text-xs">{revision.bicycle_size}</Badge>
                        </div>
                        
                        {/* Revision type */}
                        {revision.type && (
                          <div className="mt-2">
                            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                              {revision.type}
                            </Badge>
                          </div>
                        )}
                      </div>
                      
                      {/* Right side: cost */}
                      <div className="text-right">
                        <div className="text-xl font-bold text-indigo-600 font-['Barlow_Condensed']">
                          {formatCurrency(revision.total_cost)}
                        </div>
                        {revision.materials && revision.materials.length > 0 && (
                          <div className="text-xs text-slate-500 mt-1">
                            {revision.materials.length} material{revision.materials.length !== 1 ? 'es' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Materials used */}
                    {revision.materials && revision.materials.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Materiales utilizados</div>
                        <div className="flex flex-wrap gap-2">
                          {revision.materials.map((mat, mIdx) => (
                            <span 
                              key={mIdx}
                              className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded px-2 py-1 text-sm"
                            >
                              <span className="text-slate-600">{mat.material_name}</span>
                              <span className="text-slate-400">×</span>
                              <span className="font-semibold text-slate-800">{mat.quantity}</span>
                              <span className="text-slate-400 ml-1">({formatCurrency(mat.total_price)})</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Notes */}
                    {revision.notes && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Notas</div>
                        <p className="text-sm text-slate-600">{revision.notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Wrench size={48} className="mx-auto mb-4 opacity-50" />
                <p>No hay revisiones en este período</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={closeUserRevisionsModal}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
